import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API, fmtMoney, fmtDate } from "@/lib/api";
import { useAssetClasses } from "@/lib/assetClasses";
import { useAuth } from "@/lib/auth";
import AssetForm from "@/components/AssetForm";
import ShareQrDialog from "@/components/ShareQrDialog";
import MigrateAssetDialog from "@/components/MigrateAssetDialog";
import ResaleDrawer from "@/components/ResaleDrawer";
import WarrantyConciergeDrawer from "@/components/WarrantyConciergeDrawer";
import TransitionGuideDrawer from "@/components/TransitionGuideDrawer";
import { CaretLeft, Trash, PencilSimple, Sparkle, FilePdf, FileDoc, DownloadSimple, QrCode, ArrowsLeftRight, Star, Storefront, ShieldCheck, Warning, Compass } from "@phosphor-icons/react";

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite, households } = useAuth();
  const [asset, setAsset] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [estimating, setEstimating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [resaleOpen, setResaleOpen] = useState(false);
  const [warrantyOpen, setWarrantyOpen] = useState(false);
  const [transitionOpen, setTransitionOpen] = useState(false);
  const { labelFor: categoryLabel, colorFor } = useAssetClasses();

  const load = () => {
    setLoading(true);
    api
      .get(`/assets/${id}`)
      .then((r) => setAsset(r.data))
      .catch(() => toast.error("Asset not found"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onUpdate = async (payload) => {
    try {
      const res = await api.put(`/assets/${id}`, payload);
      setAsset(res.data);
      setEditing(false);
      toast.success("Entry updated.");
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Remove this asset from the ledger? This cannot be undone.")) return;
    try {
      await api.delete(`/assets/${id}`);
      toast.success("Entry struck from the ledger.");
      navigate("/assets");
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const estimate = async () => {
    if (!asset) return;
    const desc = `${asset.name} ${asset.brand || ""} ${asset.model || ""} ${asset.notes || ""} condition: ${asset.condition || "used"}`;
    setEstimating(true);
    try {
      const r = await api.post("/assets/estimate-value", { description: desc });
      const { fmv, rcv, confidence, reasoning } = r.data;
      const updated = { ...asset, fmv: fmv ?? asset.fmv, rcv: rcv ?? asset.rcv };
      await api.put(`/assets/${id}`, updated);
      setAsset(updated);
      toast.success(`Values estimated (${confidence}). ${reasoning || ""}`.slice(0, 120));
    } catch (e) {
      toast.error("Estimation failed.");
    } finally {
      setEstimating(false);
    }
  };

  const setFeatured = async (storage_path) => {
    if (!asset || !canWrite) return;
    try {
      const r = await api.post(`/assets/${id}/featured`, { storage_path });
      setAsset(r.data);
      toast.success("Featured thumbnail updated.");
    } catch (e) {
      toast.error("Couldn't set featured photo.");
    }
  };

  if (loading) return <div className="text-sm text-[#5C5F58]" data-testid="detail-loading">Loading…</div>;
  if (!asset) return <div className="text-sm text-[#5C5F58]">Not found.</div>;

  if (editing) {
    return (
      <div className="space-y-6" data-testid="asset-editing">
        <button onClick={() => setEditing(false)} className="text-sm text-[#5C5F58] hover:text-[#0A192F] inline-flex items-center gap-2">
          <CaretLeft size={14} /> Cancel edit
        </button>
        <div className="bg-white border border-[#E0DDD6] p-6 md:p-8">
          <AssetForm initial={asset} onSubmit={onUpdate} submitLabel="Save Changes" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="asset-detail" className="space-y-8">
      <div className="flex items-center justify-between">
        <Link to="/assets" className="inline-flex items-center gap-2 text-sm text-[#5C5F58] hover:text-[#0A192F]" data-testid="back-to-list">
          <CaretLeft size={14} /> Back to ledger
        </Link>
        <div className="flex gap-2 flex-wrap justify-end">
          {canWrite && (
            <button
              data-testid="estimate-btn"
              onClick={estimate}
              disabled={estimating}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[#E0DDD6] bg-white hover:border-[#1A3622] text-[#0A192F] disabled:opacity-60"
            >
              <Sparkle size={14} /> {estimating ? "Estimating…" : "AI Estimate Value"}
            </button>
          )}
          {canWrite && (
            <button
              data-testid="sell-asset-btn"
              onClick={() => setResaleOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[#9C5012] bg-[#9C5012] hover:bg-[#7A3D0D] text-[#F9F8F6]"
              title="Prep this asset for resale with Leo"
            >
              <Storefront size={14} /> Sell Asset
            </button>
          )}
          {canWrite && (
            <button
              data-testid="transition-asset-btn"
              onClick={() => setTransitionOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[#1A3622] bg-white hover:bg-[#F9F8F6] text-[#1A3622]"
              title="Plan a transition for this asset with Leo"
            >
              <Compass size={14} /> Transition
            </button>
          )}
          <button
            data-testid="tools-btn"
            onClick={() => setDialogOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[#E0DDD6] bg-white hover:border-[#1A3622] text-[#0A192F]"
          >
            <QrCode size={14} /> QR · Share
          </button>
          {canWrite && households.length > 0 && (
            <button
              data-testid="migrate-btn"
              onClick={() => setMigrateOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[#E0DDD6] bg-white hover:border-[#1A3622] text-[#0A192F]"
            >
              <ArrowsLeftRight size={14} /> Move
            </button>
          )}
          {canWrite && (
            <button
              data-testid="edit-btn"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[#E0DDD6] bg-white hover:border-[#1A3622] text-[#0A192F]"
            >
              <PencilSimple size={14} /> Edit
            </button>
          )}
          {canWrite && (
            <button
              data-testid="delete-btn"
              onClick={onDelete}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-[#6B1718] bg-white text-[#6B1718] hover:bg-[#6B1718] hover:text-white"
            >
              <Trash size={14} /> Strike
            </button>
          )}
        </div>
      </div>

      <header className="border-b border-[#C9C4B8] pb-6">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2 inline-flex items-center gap-2" data-testid="asset-detail-class-badge">
          <span
            className="inline-block w-2 h-2"
            style={{ backgroundColor: colorFor(asset.category) }}
            aria-hidden="true"
          />
          {categoryLabel(asset.category)} · Entry #{asset.id.slice(0, 8)}
        </div>
        <h1 className="font-serif-display text-5xl text-[#0A192F] leading-[0.95]">{asset.name}</h1>
        {(asset.brand || asset.model) && (
          <p className="font-mono-ledger text-sm text-[#5C5F58] mt-3">
            {[asset.brand, asset.model].filter(Boolean).join(" · ")}
          </p>
        )}
      </header>

      {/* Valuation strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#E0DDD6] bg-white">
        <Cell label="Fair Market Value" value={fmtMoney(asset.fmv)} />
        <Cell label="Replacement Cost" value={fmtMoney(asset.rcv)} />
        <Cell label="Purchase Price" value={fmtMoney(asset.purchase_price)} />
        <Cell label="Purchased" value={fmtDate(asset.purchase_date)} last />
      </div>

      {/* Warranty & Protection — surfaces when the asset has a warranty status. */}
      {asset.warranty_status && asset.warranty_status !== "none" && (
        <WarrantyBlock
          asset={asset}
          canWrite={canWrite}
          onLaunch={() => setWarrantyOpen(true)}
        />
      )}

      {/* Detail grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 bg-white border border-[#E0DDD6]">
          <SectionHeader eyebrow="Identification" title="Technical Record" />
          {asset.featured_photo_path && (
            <div
              className="border-t border-[#E0DDD6] p-6 pb-0"
              data-testid="technical-record-thumbnail"
            >
              <div className="flex gap-5 items-start">
                <Link
                  to={`#photo-${asset.featured_photo_path}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector(`[data-testid="asset-photo-${asset.featured_photo_path}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  className="block w-32 h-32 sm:w-40 sm:h-40 border border-[#C5A059] ring-2 ring-[#C5A059]/30 overflow-hidden bg-[#F9F8F6] shrink-0"
                  aria-label="Featured asset photo"
                >
                  <img
                    src={`${API}/files/${asset.featured_photo_path}`}
                    alt={asset.name}
                    className="w-full h-full object-cover"
                  />
                </Link>
                <div className="min-w-0 flex-1 pt-1">
                  <div className="inline-flex items-center gap-1 bg-[#C5A059] text-[#0A192F] px-1.5 py-0.5 text-[9px] font-mono-ledger uppercase tracking-[0.15em] mb-2">
                    <Star size={8} weight="fill" /> <span>Featured Thumbnail</span>
                  </div>
                  <p className="text-xs text-[#5C5F58] italic leading-relaxed">
                    This image represents the asset in ledger summaries, search results, and the Standing Estate Manifest.
                    {canWrite ? " Change it in the Photographs grid below." : ""}
                  </p>
                </div>
              </div>
            </div>
          )}
          <dl className="grid grid-cols-2 gap-0 divide-x divide-y divide-[#E0DDD6] border-t border-[#E0DDD6]">
            <Field k="Serial Number" v={asset.serial} mono />
            <Field
              k="Room"
              v={
                asset.location
                  ? asset.sub_location
                    ? `${asset.location} · ${asset.sub_location}`
                    : asset.location
                  : asset.sub_location || null
              }
            />
            <Field k="Condition" v={asset.condition} />
            <Field k="Provenance" v={asset.provenance} />
          </dl>
          {asset.category === "home_component" &&
            (asset.component_type ||
              asset.install_date ||
              asset.warranty_expiry ||
              asset.material ||
              asset.paint_code ||
              asset.specs ||
              asset.service_history ||
              asset.room) && (
              <div className="border-t border-[#E0DDD6]" data-testid="asset-operational">
                <div className="px-6 pt-5 pb-2 font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#1A3622]">
                  Operational Details
                </div>
                <dl className="grid grid-cols-2 gap-0 divide-x divide-y divide-[#E0DDD6] border-t border-[#E0DDD6]">
                  <Field
                    k="Component Type"
                    v={asset.component_type ? asset.component_type.replace(/_/g, " ") : null}
                  />
                  <Field k="Room" v={asset.room} />
                  <Field k="Installed" v={asset.install_date} />
                  <Field k="Warranty Expires" v={asset.warranty_expiry} />
                  <Field k="Material" v={asset.material} />
                  <Field k="Paint / Finish Code" v={asset.paint_code} mono />
                </dl>
                {asset.specs && (
                  <div className="p-6 border-t border-[#E0DDD6]">
                    <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                      Specs
                    </div>
                    <p className="text-sm text-[#0A192F] whitespace-pre-wrap leading-relaxed">{asset.specs}</p>
                  </div>
                )}
                {asset.service_history && (
                  <div className="p-6 border-t border-[#E0DDD6]">
                    <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                      Service History
                    </div>
                    <p className="text-sm text-[#0A192F] whitespace-pre-wrap leading-relaxed">{asset.service_history}</p>
                  </div>
                )}
              </div>
            )}

          {asset.notes && (
            <div className="border-t border-[#E0DDD6] p-6">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                Notes
              </div>
              <p className="text-sm text-[#0A192F] whitespace-pre-wrap leading-relaxed">{asset.notes}</p>
            </div>
          )}
        </section>

        <aside className="bg-white border border-[#E0DDD6]">
          <SectionHeader eyebrow="Estate Planning" title="Dispensation" />
          <div className="p-6 border-t border-[#E0DDD6]">
            {asset.dispensation ? (
              <div className="bg-[#F9F8F6] border-l-2 border-[#6B1718] p-4">
                <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#6B1718] mb-2">
                  Private — for executors
                </div>
                <p className="text-sm text-[#0A192F] leading-relaxed whitespace-pre-wrap">
                  {asset.dispensation}
                </p>
              </div>
            ) : (
              <p className="text-sm text-[#5C5F58]">No dispensation instructions recorded.</p>
            )}
          </div>
        </aside>
      </div>

      {/* Attachments */}
      {(asset.photo_paths?.length > 0 || asset.document_paths?.length > 0) && (
        <section className="bg-white border border-[#E0DDD6]" data-testid="attachments-view">
          <SectionHeader eyebrow="Archive" title="Attachments" />
          <div className="border-t border-[#E0DDD6] p-6 space-y-6">
            {asset.photo_paths?.length > 0 && (
              <div>
                <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-3">
                  Photographs · {asset.photo_paths.length}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="asset-photos">
                  {asset.photo_paths.map((p) => {
                    const isFeatured = asset.featured_photo_path === p;
                    return (
                      <div key={p} className={`relative group block aspect-square border ${isFeatured ? "border-[#C5A059] ring-2 ring-[#C5A059]/40" : "border-[#E0DDD6]"} bg-[#F9F8F6] overflow-hidden`}>
                        <a
                          href={`${API}/files/${p}`}
                          target="_blank"
                          rel="noreferrer"
                          className="block w-full h-full"
                          data-testid={`asset-photo-${p}`}
                        >
                          <img src={`${API}/files/${p}`} alt="Asset" className="w-full h-full object-cover" />
                        </a>
                        {isFeatured && (
                          <div
                            className="absolute bottom-1 left-1 inline-flex items-center gap-1 bg-[#C5A059] text-[#0A192F] px-1.5 py-0.5 text-[9px] font-mono-ledger uppercase tracking-[0.15em] pointer-events-none"
                            data-testid={`asset-photo-featured-${p}`}
                          >
                            <Star size={8} weight="fill" /> Featured
                          </div>
                        )}
                        {canWrite && (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setFeatured(p); }}
                            data-testid={`asset-set-featured-${p}`}
                            title={isFeatured ? "This is the featured thumbnail" : "Set as featured thumbnail"}
                            aria-label={isFeatured ? "Featured photo" : "Set as featured photo"}
                            className={`absolute top-1 left-1 w-7 h-7 flex items-center justify-center transition-colors ${
                              isFeatured
                                ? "bg-[#C5A059] text-[#0A192F]"
                                : "bg-[#0A192F]/70 text-white opacity-0 group-hover:opacity-100 hover:bg-[#C5A059] hover:text-[#0A192F]"
                            }`}
                          >
                            <Star size={14} weight={isFeatured ? "fill" : "regular"} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {asset.document_paths?.length > 0 && (
              <div>
                <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-3">
                  Documents · {asset.document_paths.length}
                </div>
                <ul className="border border-[#E0DDD6]" data-testid="asset-documents">
                  {asset.document_paths.map((p) => {
                    const filename = p.split("/").pop();
                    const isPdf = filename.toLowerCase().endsWith(".pdf");
                    const Icon = isPdf ? FilePdf : FileDoc;
                    const color = isPdf ? "#6B1718" : "#5C5F58";
                    return (
                      <li key={p} className="border-b last:border-b-0 border-[#E0DDD6]">
                        <a
                          href={`${API}/files/${p}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-3 px-4 py-3 hover:bg-[#F9F8F6] transition-colors"
                          data-testid={`asset-doc-${p}`}
                        >
                          <Icon size={18} color={color} />
                          <span className="text-sm text-[#0A192F] flex-1 truncate">{filename}</span>
                          <DownloadSimple size={14} color="#5C5F58" />
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      <ShareQrDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        assetId={asset.id}
        assetName={asset.name}
        serial={asset.serial}
      />

      <MigrateAssetDialog
        open={migrateOpen}
        onOpenChange={setMigrateOpen}
        asset={asset}
        onMigrated={() => navigate("/assets")}
      />

      <ResaleDrawer
        open={resaleOpen}
        onOpenChange={setResaleOpen}
        assetId={asset.id}
        assetName={asset.name}
      />

      <WarrantyConciergeDrawer
        open={warrantyOpen}
        onOpenChange={setWarrantyOpen}
        assetId={asset.id}
        assetName={asset.name}
        onRegistered={load}
      />

      <TransitionGuideDrawer
        open={transitionOpen}
        onOpenChange={setTransitionOpen}
        assetId={asset.id}
        assetSnapshot={{
          id: asset.id,
          name: asset.name,
          category: asset.category,
          brand: asset.brand,
          fmv: asset.fmv,
          location: asset.location,
          notes: asset.notes,
        }}
      />
    </div>
  );
}

function SectionHeader({ eyebrow, title }) {
  return (
    <header className="px-6 py-4">
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
        {eyebrow}
      </div>
      <h2 className="font-serif-display text-2xl text-[#0A192F]">{title}</h2>
    </header>
  );
}

function Cell({ label, value, last }) {
  return (
    <div className={`px-6 py-5 border-[#E0DDD6] ${last ? "" : "border-b md:border-b-0 md:border-r"}`}>
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
        {label}
      </div>
      <div className="font-mono-ledger text-xl text-[#0A192F] tabular-nums mt-2">{value}</div>
    </div>
  );
}

function Field({ k, v, mono }) {
  return (
    <div className="p-5">
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
        {k}
      </div>
      <div className={`text-sm text-[#0A192F] ${mono ? "font-mono-ledger" : ""}`}>{v || "—"}</div>
    </div>
  );
}


function WarrantyBlock({ asset, canWrite, onLaunch }) {
  const status = asset.warranty_status;
  const exp = asset.warranty_expiration_date;
  const daysLeft = exp ? Math.ceil((new Date(exp).getTime() - Date.now()) / 86400000) : null;

  let tier = "unregistered";
  if (status === "registered") {
    if (daysLeft != null && daysLeft < 0) tier = "expired";
    else if (daysLeft != null && daysLeft <= 7) tier = "expiring_urgent";
    else if (daysLeft != null && daysLeft <= 30) tier = "expiring_soon";
    else tier = "registered";
  }

  const TIER = {
    unregistered: {
      label: "UNREGISTERED · ACTION RECOMMENDED",
      pill: "bg-[#9C5012] text-[#F9F8F6]",
      border: "border-[#C5A059]",
      bg: "bg-[#F4E9D2]",
      text: "text-[#5C3D08]",
      cta: "Register via Companion",
    },
    registered: {
      label: "REGISTERED",
      pill: "bg-[#1A3622] text-[#F9F8F6]",
      border: "border-[#E0DDD6]",
      bg: "bg-white",
      text: "text-[#0A192F]",
      cta: "Update warranty record",
    },
    expiring_soon: {
      label: "EXPIRING SOON",
      pill: "bg-[#9C5012] text-[#F9F8F6]",
      border: "border-[#C5A059]",
      bg: "bg-[#F4E9D2]",
      text: "text-[#5C3D08]",
      cta: "Review warranty",
    },
    expiring_urgent: {
      label: "EXPIRES THIS WEEK",
      pill: "bg-[#6B1718] text-[#F9F8F6]",
      border: "border-[#6B1718]",
      bg: "bg-[#FAEDED]",
      text: "text-[#6B1718]",
      cta: "Review now",
    },
    expired: {
      label: "EXPIRED",
      pill: "bg-[#5C5F58] text-[#F9F8F6]",
      border: "border-[#E0DDD6]",
      bg: "bg-white",
      text: "text-[#5C5F58]",
      cta: "Acknowledge",
    },
  };
  const cfg = TIER[tier];
  const Icon = tier === "registered" ? ShieldCheck : Warning;

  return (
    <section
      className={`border ${cfg.border} ${cfg.bg} px-6 py-5`}
      data-testid="warranty-block"
      data-warranty-tier={tier}
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-9 h-9 ${cfg.pill} flex items-center justify-center shrink-0`}>
            <Icon size={18} weight="regular" />
          </div>
          <div className="min-w-0">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
              Warranty &amp; Protection
            </div>
            <div className={`font-serif-display text-xl ${cfg.text}`}>
              {tier === "registered" && exp
                ? `Covered through ${exp}`
                : tier === "expiring_soon" || tier === "expiring_urgent"
                  ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} until expiry · ${exp}`
                  : tier === "expired"
                    ? `Coverage ended ${exp}`
                    : "Manufacturer registration recommended"}
            </div>
            <div className="mt-1">
              <span className={`px-1.5 py-0.5 text-[9px] font-mono-ledger uppercase tracking-[0.15em] ${cfg.pill}`}>
                {cfg.label}
              </span>
              {asset.warranty_notes && (
                <span className="ml-2 text-xs text-[#5C5F58] italic">
                  {asset.warranty_notes}
                </span>
              )}
            </div>
          </div>
        </div>
        {canWrite && (
          <button
            onClick={onLaunch}
            data-testid="warranty-launch-block-btn"
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#2E4D77] text-[#F9F8F6] border border-[#1E3554] hover:bg-[#1E3554]"
          >
            <ShieldCheck size={14} weight="bold" /> {cfg.cta}
          </button>
        )}
      </div>
    </section>
  );
}
